import { useCallback, useEffect, useState } from "react";
import { retrieveAdminInfo, updateApprovalRequest, retrieveGames, reloadGames } from "../../api/api";
import { Table, Form, Button } from "react-bootstrap";
import { ROLE_ADMIN } from "../../constants";
import { WeekSelector } from "../weeks/WeekSelector";
import { SetScoreModal } from "./SetScoreModal";
import { SetSpreadModal } from "./SetSpreadModal";

function Admin(props) {
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [adminInfo, setAdminInfo] = useState(null);
    const [games, setGames] = useState(null);
    const [showSetScoreModal, setShowSetScoreModal] = useState(false);
    const [scoreModalGame, setScoreModalGame] = useState(null);
    const [showSetSpreadModal, setShowSetSpreadModal] = useState(false);
    const [spreadModalGame, setSpreadModalGame] = useState(null);

    const handleReloadGames = (w) => {
        if (!selectedWeek) {
            return;
        }
        reloadGames(selectedWeek.number).then((response) => {
            setGames(response);
        });
    };

    const handleSetScoreClick = (e, game) => {
        e.preventDefault();
        setScoreModalGame(game);
        setShowSetScoreModal(true);
    }

    const handleSetSpreadClick = (e, game) => {
        e.preventDefault();
        setSpreadModalGame(game);
        setShowSetSpreadModal(true);
    }

    const refreshGames = useCallback((games) => {
        if(!selectedWeek) {
            setGames([]);
            return;
        }

        if(games) {
            setGames(games)
        } else {
            retrieveGames(selectedWeek.number).then((response) => {
                setGames(response);
            }).catch(() => {
                setGames([]);
            });
        }
    }, [selectedWeek]);

    useEffect(() => {
        if (selectedWeek) {
            refreshGames();
        }
    }, [selectedWeek, refreshGames]);

    useEffect(() => {
        if (props.user && props.user.role === ROLE_ADMIN) {
            retrieveAdminInfo().then((adminInfo) => {
                setAdminInfo(adminInfo);
            });
        }
    }, [props.user, props.users]);

    const handleApprovalStatusChange = (id, status) => {
        updateApprovalRequest(id, status).then((users) => {
            props.setUsers(users);
            for(let i = 0; i < users.length; i++) {
                if(users[i].id === props.user.id) {
                    props.setUser(users[i]);
                    break;
                }
            }
        });
    };

    const approvalRequests = () => {
        return (
            <tbody>
                {props.users.map(u => {
                    return u.entry_approval_requests.filter(e => e.status === "PENDING" || e.status === "AWAITING_PAYMENT").map(e => {
                        return (
                            <tr key={'entry-approval-request-' + e.id}>
                                <td>{u.last_name}, {u.first_name}</td>
                                <td>{u.email}</td>
                                <td>{e.entry_amount}</td>
                                <td>{e.overhead_amount}</td>
                                <td>{e.description}</td>
                                <td>
                                    <Form.Select id={'entry-approval-request-status-' + e.id} onChange={(event) => handleApprovalStatusChange(e.id, event.target.value)}>
                                        <option value={e.status}>{e.status}</option>
                                        {e.status === "PENDING" && <option value="AWAITING_PAYMENT">AWAITING_PAYMENT</option>}
                                        <option value="APPROVED_AND_PAID">APPROVED_AND_PAID</option>
                                        {e.status === "PENDING" && <option value="DENIED">DENIED</option>}
                                    </Form.Select>
                                </td>
                            </tr>
                        );
                    });
                })}
            </tbody>
        );
    };

    const users = () => {
        return (
            <tbody>
                {props.users.map(u => {
                    return (
                        <tr key={'users-' + u.id}>
                            <td>{u.last_name}, {u.first_name}</td>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td>{u.balance}</td>
                            <td>{u.debt}</td>
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    const dateToString = (date) => {
        const d = new Date(Date.parse(date))
        return d.toLocaleString('en-US', { weekday: 'long' }) + ' ' + d.toLocaleString();
    };

    return (
        <>
            <div className="container">
                <div className="row">
                    <div className="column">
                        <h1 className="py-4">Admin Dashboard</h1>
                    </div>
                </div>
                <div className="row">
                    <div className="column">
                        <h2 className="py-4">Admin Info</h2>
                        {adminInfo &&
                            <ul>
                                <li key="account_balance"><strong>Account Balance:</strong> ${adminInfo.account_balance}</li>
                                <li key="total_user_debt"><strong>Total User Debt:</strong> ${adminInfo.total_user_debt}</li>
                            </ul>
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="column">
                        <h2 className="py-4">Pending Approvals</h2>
                        <Table striped bordered hover variant="dark">
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    <th>Email</th>
                                    <th>Entry Amount</th>
                                    <th>Overhead Amount</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            {approvalRequests()}
                        </Table>
                    </div>
                </div>
                <div className="row">
                    <div className="column">
                        <h2 className="py-4">Games</h2>
                        <table>
                            <tbody>
                                <tr>
                                    <td><WeekSelector weeks={props.weeks} week={selectedWeek} setWeek={setSelectedWeek} /></td>
                                    {selectedWeek && <td><Button className="ms-3" onClick={handleReloadGames}>Reload</Button></td>}
                                </tr>
                            </tbody>
                        </table>
                        <Table className="my-4" striped bordered hover variant="dark">
                            <thead>
                                <tr>
                                    <th>Start Time</th>
                                    <th>Home Team</th>
                                    <th>Points</th>
                                    <th>Away Team</th>
                                    <th>Points</th>
                                    <th>Home Spread</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    (games || []).map((g) => {
                                        return (
                                            <tr key={'game-' + g.id}>
                                                <td>{dateToString(g.start_time)}</td>
                                                <td>{g.home_team}</td>
                                                <td>{g.home_points}</td>
                                                <td>{g.away_team}</td>
                                                <td>{g.away_points}</td>
                                                <td>{g.home_spread}</td>
                                                <td><Button className="me-3" onClick={(e) => {handleSetScoreClick(e, g);}}>Set Score</Button><Button onClick={(e) => {handleSetSpreadClick(e, g);}}>Set Home Spread</Button></td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </Table>
                    </div>
                </div>
                <div className="row">
                    <div className="column">
                        <h2 className="py-4">Users</h2>
                        <Table striped bordered hover variant="dark">
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    <th>Screen Name</th>
                                    <th>Email</th>
                                    <th>Balance</th>
                                    <th>Debt</th>
                                </tr>
                            </thead>
                            {users()}
                        </Table>
                    </div>
                </div>
            </div>
            <SetScoreModal game={scoreModalGame} show={showSetScoreModal} setShow={setShowSetScoreModal} refreshGames={refreshGames} />
            <SetSpreadModal game={spreadModalGame} show={showSetSpreadModal} setShow={setShowSetSpreadModal} refreshGames={refreshGames} />
        </>
    );
}

export default Admin;